/**
 * ResumePaywallModal — the conversion-critical interstitial that fires
 * when a free-tier user clicks "New Resume" with their 1 free resume
 * already created.
 *
 * Why a modal and not a redirect to /pricing?
 *   GA tells us /pricing gets ~22 views/month vs ~1.5K events overall.
 *   The "make a second resume" moment is when the user is MOST committed
 *   (they got value from #1, want #2). Modal keeps them on the dashboard
 *   they trust, with their first resume visible in the background as
 *   social proof. Direct paywall redirect at this moment historically
 *   drops conversion (we'd lose context).
 *
 * Copy strategy (from the conversion playbook):
 *   - Hero: "Your first resume is built. Unlock unlimited from here."
 *   - Bullets: anchor on USE-CASES, not features
 *   - Founding price ($9/mo) shown prominently, with the "$2,000+ in
 *     coaching value" anchor (matches existing landing-page voice)
 *   - Primary CTA: "Unlock Unlimited — $9/mo" → /pricing
 *   - Secondary CTA: "I'll think about it" (closes — never blocks)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, ArrowRight, Check } from 'lucide-react';


const UNLOCK_BULLETS = [
  { title: 'More resumes', body: 'Basic: 10/month. Pro: no monthly cap — build a tailored resume for every role.' },
  { title: 'Unlimited AI tools', body: 'Unlimited rewrites, cover letters, interview prep, LinkedIn optimizer, and Sage coaching.' },
  { title: 'All 12 Studio themes', body: 'Premium templates + clean PDF/DOCX exports (no watermark).' },
  { title: 'Cancel anytime', body: 'Founding Member price locks in forever even after the campaign ends.' },
];


const ResumePaywallModal = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="resume-paywall-modal"
      >
        <motion.div
          className="bg-white max-w-lg w-full overflow-hidden shadow-2xl"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-[#0f1115] text-white px-6 py-5 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-[#fbbf24] mb-2">
                <Sparkles size={11} /> Unlock Ascend
              </div>
              <h2 className="font-satoshi text-xl sm:text-2xl font-black leading-tight">
                Your first resume is built.<br />
                <span className="text-[#fbbf24]">Unlock unlimited from here.</span>
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white shrink-0 -mt-1"
              data-testid="paywall-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Bullets */}
          <div className="px-6 py-5 space-y-3 bg-white" data-testid="paywall-bullets">
            {UNLOCK_BULLETS.map((b) => (
              <div key={b.title} className="flex gap-3">
                <Check size={16} className="text-[#10b981] shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-bold text-[#0f1115]">{b.title}</div>
                  <div className="text-[#4b5563]">{b.body}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Price + CTAs */}
          <div className="px-6 pb-6 pt-2">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-3xl font-black font-satoshi text-[#0f1115]">$9</span>
              <span className="text-sm text-[#6b7280]">/month, Founding Member price locked-in forever</span>
            </div>
            <div className="text-xs text-[#6b7280] mb-4">
              vs $2,000+ for a career-coaching package · cancel anytime
            </div>
            <Link
              to="/pricing?ref=resume_paywall&utm_source=dashboard&utm_medium=in_app&utm_campaign=resume_limit_modal"
              className="block w-full text-center bg-[#002fa7] text-white font-bold px-5 py-3 hover:bg-[#001f6f] transition-colors"
              data-testid="paywall-primary-cta"
              onClick={onClose}
            >
              Unlock Unlimited — $9/mo <ArrowRight size={14} className="inline ml-1" />
            </Link>
            <button
              onClick={onClose}
              className="block w-full text-center text-[#6b7280] text-xs mt-3 hover:text-[#0f1115] transition-colors"
              data-testid="paywall-secondary-cta"
            >
              I'll think about it
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default ResumePaywallModal;
